import styled from '@emotion/styled'
import { yupResolver } from '@hookform/resolvers/yup'
import ReactJson from '@microlink/react-json-view'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { useCardBalanceConfig, useCreateCardBalanceConfig, useMandateApprovalv4 } from '../../api/react-query'
import { AutogiroApprovalState } from '../../api/swagger/definitions/backoffice'
import { TextFieldController } from '../../components/react-hook-components.tsx'
import TextContainer from '../../components/TextContainer.tsx'
import { TooltipChangeAccountStatus } from '../../components/Tooltips.tsx'
import ButtonIconChangeStatus from '../../figma/images/buttonIconChangeStatus.tsx'
import Spacings from '../../figma/tokens/Spacings.ts'
import { BorderContainer, BorderContainerHeader } from '../../helpers/CreditOnCardHelpers.tsx'
import { captureException } from '../../helpers/sentryHelpers.ts'
import useMaterialNotification from '../../hooks/useMaterialNotification.tsx'
import FigmaBox from '../../mynt-components/components/FigmaBox.tsx'
import FigmaButton from '../../mynt-components/components/FigmaButton.tsx'
import FigmaStatusBox, { FigmaFieldStatusTypes } from '../../mynt-components/components/FigmaStatusBox.tsx'
import MaterialSwitch from '../../mynt-components/components/MaterialSwitch.tsx'
import { GenericObjectViewContainer, Line, UnstyledButton } from '../../mynt-components/components/StyledComponents.tsx'
import { BO_TWO_COLUMN_SIZE_LEFT, BO_TWO_COLUMN_SIZE_RIGHT } from '../../mynt-components/WeakHardCodedSizes.ts'
import { getText, TextKeys } from '../../tiger/libs/TextRepository.ts'

type Props = {
  customerId: string
  setIsChangeStatusModalOpen?: React.Dispatch<React.SetStateAction<boolean>>
}

const DEFAULT = 'default'

type FormValues = { threshold?: number; topUpValue?: number }

const negativeNumberErrorMessage = getText(TextKeys.mFautoTopUpFallsBelowError, 'figma')
const numberErrorMessage = TextKeys.autoTopUpModalformAddaNumber.characters

const ValidationSchema = Yup.object().shape({
  threshold: Yup.number().required(numberErrorMessage).typeError(numberErrorMessage),
  topUpValue: Yup.number().required(negativeNumberErrorMessage).typeError(negativeNumberErrorMessage).min(0, negativeNumberErrorMessage),
})

export default function CustomerCardTabAutoTopup({ customerId, setIsChangeStatusModalOpen }: Props) {
  const notify = useMaterialNotification()

  const [isMutating, setIsMutating] = useState<boolean>(false)

  const { data: autoGiroApproval, isLoading: isLoadingAutoGiro } = useMandateApprovalv4(customerId)
  const { data: cardBalanceConfig, isLoading: isLoadingCardBalanceConfig } = useCardBalanceConfig(customerId)

  const { control, handleSubmit } = useForm<FormValues>({
    values: { threshold: cardBalanceConfig?.topUp?.threshold, topUpValue: cardBalanceConfig?.topUp?.topUpValue },
    resolver: yupResolver(ValidationSchema),
  })

  const directDebitStatus = {
    [AutogiroApprovalState.APPROVED]: FigmaFieldStatusTypes.ACTIVE,
    [AutogiroApprovalState.FAILED]: FigmaFieldStatusTypes.ERROR,
    [AutogiroApprovalState.PENDING]: FigmaFieldStatusTypes.PENDING,
    [AutogiroApprovalState.REQUESTED]: FigmaFieldStatusTypes.REQUESTED,
    [DEFAULT]: FigmaFieldStatusTypes.INACTIVE,
  }[autoGiroApproval?.state || DEFAULT]

  const autoTopupStatusSetByCustomer = cardBalanceConfig?.topUp?.active
  const mutation = useCreateCardBalanceConfig(customerId)

  const onToggleAutotopup = async () => {
    try {
      setIsMutating(true)

      await mutation.mutateAsync({
        ...cardBalanceConfig,
        topUp: {
          ...cardBalanceConfig?.topUp,
          active: !cardBalanceConfig?.topUp?.active,
        },
      })
    } catch (error) {
      if (error instanceof Error) {
        captureException(error)
      } else {
        throw error
      }
    } finally {
      setIsMutating(false)
    }
  }

  const onSubmit = async ({ threshold, topUpValue }: FormValues) => {
    try {
      await mutation.mutateAsync({
        ...cardBalanceConfig,
        topUp: {
          ...cardBalanceConfig?.topUp,
          threshold,
          topUpValue,
        },
      })

      notify('Successfully set auto top-up limit', 'success')
    } catch (error) {
      if (error instanceof Error) {
        captureException(error)
      } else {
        throw error
      }
    }
  }

  const bankAccountValue = (() => {
    switch (autoGiroApproval?.type) {
      case 'AUTOGIRO':
        return autoGiroApproval.bankAccount
      case 'SEPA_DIRECT_DEBIT':
        return autoGiroApproval.iban
      case 'OVERFORSELSSERVICE':
        return autoGiroApproval.bankAccount
      case undefined:
        return ''
    }
  })()

  if (isLoadingAutoGiro || isLoadingCardBalanceConfig) {
    return null
  }

  return (
    <BorderContainer>
      <BorderContainerHeader>
        <TextContainer textKey={TextKeys.autoTopUpHeading} />

        <TooltipChangeAccountStatus
          content={
            <UnstyledButton onClick={() => setIsChangeStatusModalOpen && setIsChangeStatusModalOpen(true)}>
              <ButtonIconChangeStatus />
            </UnstyledButton>
          }
        />
      </BorderContainerHeader>

      <FigmaBox fullWidth direction="row" justify="space-between" align="stretch" gap={Spacings.medium}>
        <FigmaBox fullWidth direction="column">
          <FigmaBox top={Spacings.small} bottom={Spacings.medium}>
            <TextContainer textKey={TextKeys.autoTopUpSetLimitLabel} />
          </FigmaBox>

          <FormContainer fullWidth bottom direction="column">
            <TextFieldController control={control} name="threshold" type="number" labelTextKey={TextKeys.mFautoTopUpFallsBelowLabel} />
            <FigmaBox top fullWidth>
              <TextFieldController control={control} name="topUpValue" type="number" labelTextKey={TextKeys.mFautoTopUpToLabel} />
            </FigmaBox>
            <FigmaBox top fullWidth>
              <FigmaButton onClick={handleSubmit(onSubmit)} textKey={TextKeys.autoTopUpSetLimitButton} />
            </FigmaBox>
          </FormContainer>
        </FigmaBox>

        <Line vertical />

        <GenericObjectViewContainer fullWidth>
          <TextContainer textKey={TextKeys.customersCardAutoTopUpAutoTopUpDetailsHeading} />
          <FigmaBox top={Spacings.medium} bottom={Spacings.medium}>
            <TextContainer textKey={TextKeys.autoTopUpDirectDebitApproved} />
            {directDebitStatus && (
              <FigmaBox top={Spacings.min}>
                <FigmaStatusBox status={directDebitStatus} />
              </FigmaBox>
            )}
          </FigmaBox>
          <FigmaBox fullWidth direction="row" justify="space-between" align="flex-start" gap={Spacings.minimum} bottom={Spacings.medium}>
            <FigmaBox fullWidth flex={BO_TWO_COLUMN_SIZE_LEFT}>
              <TextContainer textKey={TextKeys.autoTopUpCustomerSetDetails} />
            </FigmaBox>
            <FigmaBox fullWidth flex={BO_TWO_COLUMN_SIZE_RIGHT} align="flex-end">
              <MaterialSwitch checked={!!autoTopupStatusSetByCustomer} onChange={onToggleAutotopup} disabled={isMutating} />
            </FigmaBox>
          </FigmaBox>
          <FigmaBox bottom={Spacings.min}>
            <TextContainer textKey={TextKeys.autoTopUpDetailsBankAccount} />
          </FigmaBox>
          <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccountValue} text={bankAccountValue} />
        </GenericObjectViewContainer>
      </FigmaBox>

      <FigmaBox top spacing={Spacings.huge}>
        <ReactJson src={autoGiroApproval || {}} name="" />
      </FigmaBox>
    </BorderContainer>
  )
}

const FormContainer = styled(FigmaBox)`
  min-width: 300px;
`
